/**
 * Pricing Redux Selectors
 * Memoized selectors for derived data
 */

import { createSelector } from '@ufc/core'
import type { PricingState, PricedProduct, PricingProfile, Category } from '../domain/types'
import {
  filterByCategory,
  filterBySearch,
  filterWithPrices,
  filterActive,
  sortByName,
  sortByPrice,
  getUniqueCategories,
} from '../domain/utils'

// Root state selector (assumes pricing is mounted at state.pricing)
export interface RootState {
  pricing: PricingState
}

const selectPricingState = (state: RootState): PricingState => state.pricing

// Base selectors
export const selectProfile = createSelector(
  selectPricingState,
  (pricing): PricingProfile | null => pricing.profile
)

export const selectProducts = createSelector(
  selectPricingState,
  (pricing): PricedProduct[] => pricing.products
)

export const selectSelectedProduct = createSelector(
  selectPricingState,
  (pricing): PricedProduct | null => pricing.selectedProduct
)

export const selectPricingStatus = createSelector(
  selectPricingState,
  (pricing) => pricing.status
)

export const selectPricingError = createSelector(
  selectPricingState,
  (pricing) => pricing.error
)

export const selectIsProfileLoaded = createSelector(
  selectPricingState,
  (pricing) => pricing.isProfileLoaded
)

export const selectIsProductsLoaded = createSelector(
  selectPricingState,
  (pricing) => pricing.isProductsLoaded
)

export const selectIsLoading = createSelector(
  selectPricingState,
  (pricing) => pricing.status === 'loading'
)

// Derived selectors

/**
 * Select active products only
 */
export const selectActiveProducts = createSelector(
  selectProducts,
  (products): PricedProduct[] => filterActive(products)
)

/**
 * Select products with prices
 */
export const selectProductsWithPrices = createSelector(
  selectActiveProducts,
  (products): PricedProduct[] => filterWithPrices(products)
)

/**
 * Select products sorted by name (English)
 */
export const selectProductsSortedByName = createSelector(
  selectProductsWithPrices,
  (products): PricedProduct[] => sortByName(products, 'en')
)

/**
 * Select products sorted by price (ascending)
 */
export const selectProductsSortedByPrice = createSelector(
  selectProductsWithPrices,
  (products): PricedProduct[] => sortByPrice(products, 'asc')
)

/**
 * Select unique categories from products
 */
export const selectCategories = createSelector(
  selectProducts,
  (products): Category[] => getUniqueCategories(products)
)

/**
 * Select user's tenant (from profile)
 */
export const selectTenant = createSelector(
  selectProfile,
  (profile) => profile?.tenant ?? null
)

/**
 * Select user's customer group (from profile)
 */
export const selectCustomerGroup = createSelector(
  selectProfile,
  (profile) => profile?.customerGroup ?? null
)

/**
 * Select user's price list (from profile)
 */
export const selectPriceList = createSelector(
  selectProfile,
  (profile) => profile?.priceList ?? null
)

/**
 * Select user's channel (from tenant)
 */
export const selectChannel = createSelector(
  selectTenant,
  (tenant) => tenant?.channel ?? 'ecommerce'
)

/**
 * Select currency (from price list or default)
 */
export const selectCurrency = createSelector(
  selectPriceList,
  (priceList) => priceList?.currency ?? 'SAR'
)

/**
 * Check if user has a profile loaded
 */
export const selectHasProfile = createSelector(
  selectProfile,
  (profile) => profile !== null
)

/**
 * Check if user is authenticated (has tenant)
 */
export const selectIsAuthenticated = createSelector(
  selectTenant,
  (tenant) => tenant !== null
)

/**
 * Select price list name for display
 */
export const selectPriceListName = createSelector(
  selectPriceList,
  (priceList) => priceList?.name ?? null
)

/**
 * Select customer group name for display
 */
export const selectCustomerGroupName = createSelector(
  selectCustomerGroup,
  (customerGroup) => customerGroup?.name ?? null
)

/**
 * Select pricing context for order modal
 * All derived data needed for displaying prices
 */
export const selectPricingContext = createSelector(
  [selectCurrency, selectPriceListName, selectCustomerGroupName, selectIsProfileLoaded],
  (currency, priceListName, customerGroupName, isProfileLoaded) => ({
    currency,
    priceListName,
    customerGroupName,
    isProfileLoaded,
  })
)

/**
 * Factory selector: filter products by category code
 */
export const makeSelectProductsByCategory = (categoryCode: string) =>
  createSelector(selectProductsWithPrices, (products): PricedProduct[] =>
    filterByCategory(products, categoryCode)
  )

/**
 * Factory selector: search products by term
 */
export const makeSelectProductsBySearch = (searchTerm: string) =>
  createSelector(selectProductsWithPrices, (products): PricedProduct[] =>
    filterBySearch(products, searchTerm)
  )

/**
 * Factory selector: get product by documentId
 */
export const makeSelectProductById = (documentId: string) =>
  createSelector(selectProducts, (products): PricedProduct | undefined =>
    products.find((p) => p.documentId === documentId)
  )
